import { Environment, getEnvironment } from '../Environment';

import {
  FakeLaunchDarklyProviderHoc,
  RealLaunchDarklyProviderHoc,
  getLaunchDarklyClientId,
} from './internal';

/**
 * FeatureFlagProviderHoc configures a LaunchDarkly provider with the correct
 * client ID and initial LDUser object from the cookie state of the first
 * request to handle both signed-in and anonymous users. To do so, it needs the
 * flag state from a remote service (LaunchDarkly's in production, our local
 * backend during development), and so returns a promise for the actual Provider
 * object.
 *
 * It provides the @{link
 * https://docs.launchdarkly.com/sdk/client-side/react/react-web#hooks |
 * `useFlags` hook} and that's likely to handle your use case.
 *
 * See the API docs of
 * {@link https://docs.launchdarkly.com/sdk/client-side/react/react-web | launchdarkly-react-client-sdk }
 * for details on other hooks available.
 *
 * Informing LaunchDarkly of users' transitions from anonymous to signed-in and
 * back are handled in newAuthState using {@link useLaunchDarklyUpdateUser}.
 *
 * In test and local development environments, it creates a fake LaunchDarkly
 * provider that doesn't connect to the LaunchDarkly service, but does use an
 * HTTP API on our backend to coordinate flag values with same said backend.
 */
export async function FeatureFlagProviderHoc(): Promise<React.FC | React.ElementType> {
  // We can't use the useCookies hook here because we need the cookie
  // state before the app is rendered.
  const env = getEnvironment();
  switch (env) {
    case Environment.PRODUCTION:
    case Environment.STAGING:
    case Environment.EPHEMERAL:
    case Environment.TESTING:
      // Always use a real LaunchDarkly client for these. TESTING is actually
      // All of Us's staging-ish environment.
      return RealLaunchDarklyProviderHoc(getLaunchDarklyClientId(env));
    case Environment.LOCAL:
    case Environment.UNKNOWN:
    default:
      if (process.env.REACT_APP_LAUNCHDARKLY_FORCE_LOCAL_REQUESTS_TO_CLIENT_ID != null) {
        // Frameworks and other folks sometimes need to see how LaunchDarkly
        // behaves during local development, and so they can use this env var to
        // specify the client ID of the LaunchDarkly env they want to use.
        return RealLaunchDarklyProviderHoc(
          process.env.REACT_APP_LAUNCHDARKLY_FORCE_LOCAL_REQUESTS_TO_CLIENT_ID
        );
      }
      return FakeLaunchDarklyProviderHoc();
  }
}
